import { apiRequest } from '@/lib/api-client';
import type { AIToolType } from '@/types/common';

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

export interface SendMessageResponse {
  message: string;
  conversation_id: string;
  tool_type: AIToolType;
  // Phase 7 #118 (R125). Set when the BE successfully parsed + persisted a
  // deal-review JSON payload from a tool_type='deal_review' chat turn.
  deal_review_id?: string;
}

export interface AIConversation {
  id: string;
  creator_id: string;
  tool_type: AIToolType;
  messages: ConversationMessage[];
  created_at: string;
  updated_at: string;
}

export function sendMessage(
  toolType: AIToolType,
  message: string,
  conversationHistory?: ConversationMessage[],
  additionalContext?: Record<string, unknown>,
  extras?: { deal_id?: string }
): Promise<SendMessageResponse> {
  return apiRequest<SendMessageResponse>('/api/ai/chat', {
    method: 'POST',
    body: JSON.stringify({
      tool_type: toolType,
      message,
      conversation_history: conversationHistory ?? [],
      ...(additionalContext ? { additional_context: JSON.stringify(additionalContext) } : {}),
      ...(extras?.deal_id ? { deal_id: extras.deal_id } : {}),
    }),
  });
}

export function getConversations(): Promise<AIConversation[]> {
  return apiRequest<AIConversation[]>('/api/ai/conversations');
}

export function getConversation(id: string): Promise<AIConversation> {
  return apiRequest<AIConversation>(`/api/ai/conversations/${id}`);
}

// Newest conversation (INCLUDING messages) for the current user + tool_type, or
// { conversation: null } when there's no history yet. Typed to match the actual
// GET /api/ai/conversations/latest envelope — do NOT model this on the
// getConversations/getConversation wrappers above, whose declared return types
// do not match their endpoints.
export function getLatestConversation(
  toolType: AIToolType
): Promise<{ conversation: AIConversation | null }> {
  return apiRequest<{ conversation: AIConversation | null }>(
    `/api/ai/conversations/latest?tool_type=${encodeURIComponent(toolType)}`
  );
}

// ── Content Lab ─────────────────────────────────────────────────────────────

export interface ContentIdea {
  platform: string;
  hook: string;
  concept: string;
  caption_starter: string;
  best_time: string;
  hashtags: string[];
}

export interface GenerateIdeasParams {
  platforms: string[];
  category: string;
  niche: string;
  brand_context?: string;
}

export interface GenerateIdeasResponse {
  ideas: ContentIdea[];
  usage: {
    messages_today: number;
    messages_remaining: number;
    cost_this_request: number;
  };
}

export function generateContentIdeas(
  params: GenerateIdeasParams
): Promise<GenerateIdeasResponse> {
  return apiRequest<GenerateIdeasResponse>('/api/ai/content-ideas', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}
